if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // MPR : le formulaire Rise est en « novalidate » ; on vérifie nous-mêmes les champs
        // de personnalisation rattachés au formulaire (prénom obligatoire, longueur…).
        if (!this.mprValidate()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        // MPR : un champ de personnalisation facultatif laissé vide (ex. prénom) n'est pas envoyé,
        // pour ne jamais ajouter de propriété vide à la ligne de commande.
        Array.from(formData.keys()).forEach((key) => {
          if (!key.startsWith('properties[')) return;
          const values = formData.getAll(key);
          if (values.every((value) => typeof value === 'string' && value.trim() === '')) formData.delete(key);
        });
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // MPR : compléments cochés (vrais produits Shopify) ajoutés dans la même requête
        const mprAddons = this.mprAddonItems();
        if (mprAddons.length) {
          const mainItem = {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity') || '1', 10),
            properties: {},
          };
          for (const [key, value] of formData.entries()) {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match && typeof value === 'string' && value !== '') mainItem.properties[match[1]] = value;
            if (key === 'selling_plan' && value) mainItem.selling_plan = value;
          }
          const payload = { items: [mainItem, ...mprAddons] };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            // MPR : réponse multi-articles → on expose le premier article comme Rise l'attend
            if (response.items && response.items.length && !response.id) {
              response.id = response.items[0].id;
              response.key = response.items[0].key;
            }
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            // MPR : message lisible en cas de coupure réseau ou de réponse inattendue
            this.error = false;
            this.handleErrorMessage((window.mprStrings && window.mprStrings.networkError) || window.cartStrings.error);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      mprValidate() {
        const fields = Array.from(this.form.elements).filter(
          (el) => el.willValidate && el.closest('mpr-personalization')
        );
        const invalid = fields.find((el) => !el.checkValidity());
        if (!invalid) return true;
        invalid.dispatchEvent(new CustomEvent('mpr:invalid', { bubbles: true }));
        invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalid.focus({ preventScroll: true });
        invalid.reportValidity();
        return false;
      }

      mprAddonItems() {
        // getAttribute : form.id renverrait le champ <input name="id"> du formulaire
        const formId = this.form.getAttribute('id');
        const root = this.closest('product-info') || document;
        return Array.from(root.querySelectorAll(`mpr-addons[data-form-id="${formId}"] [data-mpr-addon]:checked`)).map(
          (input) => {
            const item = { id: input.dataset.variantId, quantity: 1 };
            const extra = input.closest('li')?.querySelector('[data-mpr-addon-prop]');
            if (extra && extra.value.trim() !== '') {
              item.properties = { [extra.dataset.mprAddonProp]: extra.value.trim() };
            }
            return item;
          }
        );
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
